import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, DollarSign, Users } from "lucide-react";
import { COMPANY, STATS } from "../lib/constants";
import { PartnerLogos } from "../components/sections/PartnerLogos";
import { ServicesGrid } from "../components/sections/ServicesGrid";
import { Testimonials } from "../components/sections/Testimonials";
import { CtaBanner } from "../components/sections/CtaBanner";
import { TickerCounter } from "../components/sections/TickerCounter";

// Anchor for deterministic counters: site reference start (UTC).
// Values stay stable across reloads and grow with real-time elapsed.
const TICKER_ANCHOR = Date.parse("2025-12-01T00:00:00Z");

const HOME_HERO_BG =
  "https://images.unsplash.com/photo-1762279389042-9439bfb6c155?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxmdXR1cmlzdGljJTIwYWJzdHJhY3QlMjBkYXRhJTIwZGFya3xlbnwwfHx8fDE3Nzc1NzQ3MDd8MA&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] flex items-center overflow-hidden"
      >
        {/* Animated aurora orbs (purple+black depth) */}
        <div className="rm-aurora rm-aurora-1" aria-hidden="true" />
        <div className="rm-aurora rm-aurora-2" aria-hidden="true" />
        <div className="rm-aurora rm-aurora-3" aria-hidden="true" />

        <div className="rm-glow-top-right" />
        <div className="rm-glow-bottom-left" />
        <div className="absolute inset-0 rm-grid-bg opacity-60" aria-hidden="true" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.14]"
          style={{ backgroundImage: `url(${HOME_HERO_BG})` }}
          aria-hidden="true"
        />

        {/* Diagonal light streaks — both directions */}
        <div className="rm-streak rm-streak-1" aria-hidden="true" />
        <div className="rm-streak rm-streak-2" aria-hidden="true" />
        <div className="rm-streak rm-streak-3" aria-hidden="true" />
        <div className="rm-streak rm-streak-4" aria-hidden="true" />
        <div className="rm-streak rm-streak-5" aria-hidden="true" />

        {/* Sweeping scan line — fires once on load */}
        <div className="rm-hero-scan" aria-hidden="true" />

        {/* Vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0c]/60 to-[#0a0a0c]" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 py-28">
          <div className="lg:col-span-8 rm-fade-up">
            <div className="rm-overline mb-6 flex items-center gap-3">
              <span className="h-2 w-2 rounded-full bg-violet-500 animate-pulse" />
              Lead Generation & Consulting
            </div>
            <h1 className="font-display text-[3rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-black leading-[0.95] tracking-tighter text-white">
              Impactful marketing.{" "}
              <span className="text-violet-400">Real</span> results.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-zinc-400 max-w-2xl leading-relaxed">
              RevMatrix is a Victoria, B.C. performance marketing & consulting
              agency. Our team creates, optimizes and monitors cross channel
              campaigns on Facebook, Instagram, TikTok, YouTube, Snapchat, and
              Google to bring leads and customers straight to your business —
              no jargon, no fluff, no nonsense.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="rm-btn-primary"
              >
                Book a free first call <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="rm-btn-secondary"
              >
                Explore services
              </Link>
            </div>

            <div className="mt-14 flex items-center gap-8 text-xs font-mono text-zinc-500">
              <span>No long-term lock-ins</span>
              <span className="text-zinc-700">/</span>
              <span>Free first call</span>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:flex flex-col justify-end gap-4">
            {/* Launch — static */}
            <div
              data-testid="hero-badge-launch"
              className="rm-card rm-hero-badge p-5 flex items-start gap-4"
            >
              <Zap className="h-5 w-5 text-violet-400 mt-1" />
              <div>
                <div className="rm-overline mb-1">Launch</div>
                <div className="font-display text-2xl font-bold text-white">
                  14 days
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  from kickoff to your first ads going live
                </div>
              </div>
            </div>

            {/* Revenue — live ticker */}
            <div
              data-testid="hero-badge-revenue"
              className="rm-card rm-hero-badge p-5 flex items-start gap-4"
            >
              <DollarSign className="h-5 w-5 text-violet-400 mt-1" />
              <div className="min-w-0">
                <div className="rm-overline mb-1 flex items-center gap-2">
                  <span>Revenue Generated</span>
                  <span className="rm-live-dot" aria-hidden="true" />
                </div>
                <div className="font-display text-2xl font-bold text-white">
                  <TickerCounter
                    testId="ticker-revenue"
                    mode="randomInterval"
                    baseValue={75312480}
                    incrementMin={20}
                    incrementMax={50}
                    minIntervalMs={10000}
                    maxIntervalMs={50000}
                    storageKey="rm_revenue_counter_v1"
                    prefix="$"
                  />
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  client revenue tracked · live
                </div>
              </div>
            </div>

            {/* Leads — live ticker */}
            <div
              data-testid="hero-badge-leads"
              className="rm-card rm-hero-badge p-5 flex items-start gap-4"
            >
              <Users className="h-5 w-5 text-violet-400 mt-1" />
              <div className="min-w-0">
                <div className="rm-overline mb-1 flex items-center gap-2">
                  <span>Leads Generated</span>
                  <span className="rm-live-dot" aria-hidden="true" />
                </div>
                <div className="font-display text-2xl font-bold text-white">
                  <TickerCounter
                    testId="ticker-leads"
                    mode="randomInterval"
                    baseValue={1000000}
                    incrementBy={1}
                    minIntervalMs={5000}
                    maxIntervalMs={30000}
                    storageKey="rm_leads_counter_v1"
                  />
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  qualified leads delivered · live
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PartnerLogos />

      {/* STATS */}
      <section
        data-testid="stats-section"
        className="relative py-24 border-b border-zinc-800/70"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                data-testid={`stat-${i}`}
                className="rm-card p-8"
              >
                <div className="font-display text-4xl md:text-5xl font-black tracking-tighter text-violet-400">
                  {s.value}
                </div>
                <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mt-3">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section
        data-testid="services-preview"
        className="relative py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <div className="rm-overline mb-4">Services</div>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-white">
                How we bring in your next customers.
              </h2>
              <p className="mt-5 text-zinc-400 leading-relaxed">
                Lead-generation ads across Facebook, Instagram, TikTok,
                YouTube, Snapchat, and Google — plus consulting for businesses
                who want a clear plan before they invest ad spend.
              </p>
            </div>
            <Link
              to="/services"
              data-testid="services-all-link"
              className="rm-btn-secondary self-start"
            >
              View all services <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <ServicesGrid />
        </div>
      </section>

      <Testimonials />

      <CtaBanner />
    </div>
  );
}
